import { useMutation, useReactiveVar } from '@apollo/client';
import { useNavigate, useParams } from '@tanstack/react-router';
import type React from 'react';
import { useContext } from 'react';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import {
  ADD_TO_GALLERY_SLUG,
  ADD_TO_PLAYLIST_SLUG,
  ASSET,
  CAN_DELETE_SLUG,
  CAN_DOWNLOAD_SLUG,
  CAN_EDIT_SLUG,
  CAN_SHARE_SLUG,
  GALLERYS_ITEM_ACTION_MAP,
  HIRES_SLUG,
  HOME_PATH,
  INSERT_MODE,
  MANAGE_GALLERY_SLUG,
  REMOVE_FROM_GALLERY_SLUG,
  SELECTED_ITEMS,
} from 'src/constants';
import CreatePlayListContext, { type PlaylistAsset } from 'src/context/CreatePlayListContext';
import viewStateVar from 'src/store/viewState';
import type { ActionOption, GalleryRouteParams } from '../../types';
import { ActionPermsMenu } from './ActionPermsMenu';

interface AssetActionsPermMenuProps {
  itemId: string;
  moreActions?: ActionOption[];
  isStatic?: boolean;
}

export const AssetActionsPermMenu: React.FC<AssetActionsPermMenuProps> = ({
  itemId,
  moreActions = [],
  isStatic = false,
}) => {
  const { setCreateAddedAssets } = useContext(CreatePlayListContext);
  const [addItemToCollection] = useMutation(editCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as GalleryRouteParams;
  const viewState = useReactiveVar(viewStateVar);

  const handleActionClick = (option: ActionOption, itemId: string): void => {
    switch (option.slug) {
      case CAN_SHARE_SLUG:
        viewStateVar({
          ...viewStateVar(),
          shareDialog: itemId,
          shareContent: ASSET,
        });
        break;
      case MANAGE_GALLERY_SLUG:
        viewStateVar({
          ...viewStateVar(),
          globalAction: MANAGE_GALLERY_SLUG,
          selectedAssets: [itemId],
        });
        break;
      case ADD_TO_GALLERY_SLUG:
        viewStateVar({
          ...viewStateVar(),
          globalAction: ADD_TO_GALLERY_SLUG,
          selectedAssets: [itemId],
        });
        break;
      case REMOVE_FROM_GALLERY_SLUG:
        viewStateVar({
          ...viewStateVar(),
          globalAction: REMOVE_FROM_GALLERY_SLUG,
          selectedAssets: [itemId],
        });
        break;
      case CAN_DOWNLOAD_SLUG:
        viewStateVar({
          ...viewStateVar(),
          libraryDownloadDialog: itemId,
        });
        break;
      case HIRES_SLUG:
        viewStateVar({
          ...viewStateVar(),
          hiresDialog: SELECTED_ITEMS,
          selectedAssets: [...viewStateVar().selectedAssets, itemId],
        });
        break;
      case CAN_DELETE_SLUG:
        viewStateVar({
          ...viewStateVar(),
          deleteDialog: itemId,
          ...(viewStateVar().selectedAssets.indexOf(itemId) === -1 && {
            selectedAssets: [...viewStateVar().selectedAssets, itemId],
          }),
        });
        break;
      case ADD_TO_PLAYLIST_SLUG:
        viewStateVar({
          ...viewStateVar(),
          showRightSidebar: true,
          showHorizontal: viewStateVar().showRightSidebar && false,
          showLeftSidebar: viewStateVar().showRightSidebar && false,
          rightDrawerWidth: viewStateVar().showRightSidebar
            ? viewState.defaultRightDrawerWidth
            : viewState.minRightDrawerWidth,
        });

        if (viewState.openEditCollection && viewState.selectedCollectionId) {
          addItemToCollection({
            variables: {
              collectionId: viewState.selectedCollectionId,
              assetIds: [itemId],
              collectionType: 'PLAYLIST',
              autoSeq: true,
            },
            refetchQueries: [assetsByCollectionIdPlaylist],
          });
          return;
        }

        if (!viewState.openEditCollection) {
          viewStateVar({
            ...viewStateVar(),
            openNewCollection: true,
          });
          setCreateAddedAssets((prev) => [...prev, { id: itemId } as PlaylistAsset]);
          return;
        }
        break;
      case CAN_EDIT_SLUG:
        viewStateVar({
          ...viewStateVar(),
          editClip: itemId,
          mode: INSERT_MODE,
        });
        if (!window.location.pathname.includes(`media-page/`)) {
          navigate({ to: `${HOME_PATH}/media-page/${itemId}` });
        }
        break;
      default:
        alert('action not supported');
    }
  };

  return (
    <ActionPermsMenu
      itemId={itemId}
      parentCollectionId={params.galleryId}
      handleActionClick={handleActionClick}
      moreActions={[...moreActions]}
      permissionMap={params.galleryId ? GALLERYS_ITEM_ACTION_MAP : null}
      isStatic={isStatic}
    />
  );
};

export default AssetActionsPermMenu;
